import { useGetList } from "ra-core";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { DealsChart } from "./DealsChart";
import { HotContacts } from "./HotContacts";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

export const Dashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalDeal, isPending: isPendingDeal } = useGetList<Contact>(
    "deals",
    {
      pagination: { page: 1, perPage: 1 },
    },
  );

  const isPending = isPendingContact || isPendingContactNotes || isPendingDeal;

  if (isPending) {
    return null;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="space-y-6">
      <section className="relative isolate overflow-hidden rounded-[1.75rem] bg-[#d76f4a] px-6 py-7 text-[#fff9f1] shadow-[0_18px_45px_rgba(170,82,53,0.2)] sm:px-8 sm:py-9">
        <div className="absolute -right-16 -top-24 size-64 rounded-full border-[28px] border-[#f4c0a4]/35" />
        <div className="absolute bottom-[-95px] right-28 size-48 rounded-full bg-[#f4c0a4]/25" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#ffe8d9]">
              <Sparkles className="size-4" />
              Wabi Sabi workspace
            </div>
            <h2 className="max-w-xl text-3xl font-semibold tracking-[-0.045em] sm:text-4xl">
              The relationships that matter, in one calm place.
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-6 text-[#fff1e7]">
              Shape your day around your customers, conversations, and the
              opportunities ahead.
            </p>
          </div>
          <Link
            to="/contacts/create"
            className="inline-flex w-fit items-center gap-2 rounded-full bg-[#fff9f1] px-4 py-2.5 text-sm font-semibold text-[#a84f35] no-underline shadow-sm transition-transform hover:-translate-y-0.5"
          >
            Add a contact <ArrowUpRight className="size-4" />
          </Link>
        </div>
      </section>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        <div className="md:col-span-3">
          <div className="flex flex-col gap-4">
            {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
            <HotContacts />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="flex flex-col gap-6">
            {totalDeal ? <DealsChart /> : null}
            <DashboardActivityLog />
          </div>
        </div>

        <div className="md:col-span-3">
          <TasksList />
        </div>
      </div>
    </div>
  );
};
